import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import styled from "@emotion/styled";
import { useDispatch } from "react-redux";
import { addInvoiceOrphans, getInvoiceOrphans, getInvoices } from "../redux/ducks/invoices";
import { statusColor } from "../utils/statusColors";
import { formatDate, formatDayShort, formatMonthKey } from "../utils/dateUtils";

const INVOICE_PAGE_SIZE = 50;
const CHARGE_BUCKET = "__charges__";
const FLAT_BILLING_TYPES = new Set(["Internal", "Non Billable", "Fixed Price", "Retainer"]);
const POOL_BILLING_TYPES = new Set(["Retainer", "Hourly"]);

const DialogBody = styled(DialogContent)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
}));

const HelperLine = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm
}));

const Panel = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5)
}));

const PanelLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
    textTransform: "uppercase",
    letterSpacing: "0.04em"
}));

const PanelValue = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold
}));

const InvoiceList = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    maxHeight: 340,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column"
}));

const InvoiceRow = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$disabled" && prop !== "$selected"
})(({ theme, $disabled, $selected }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: $disabled ? "not-allowed" : "pointer",
    opacity: $disabled ? 0.55 : 1,
    background: $selected ? theme.palette.action.selected : "transparent",
    "&:last-of-type": {
        borderBottom: "none"
    },
    "&:hover": {
        background: $disabled ? "transparent" : theme.palette.action.hover
    }
}));

const InvoiceMain = styled(Box)({
    flex: 1,
    minWidth: 0
});

const InvoiceHeadline = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap"
}));

const InvoiceNumber = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold
}));

const InvoiceMeta = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
    display: "block"
}));

const InvoiceAmount = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    whiteSpace: "nowrap",
    paddingTop: theme.spacing(1)
}));

const StatusChip = styled(Chip, {
    shouldForwardProp: (prop) => prop !== "$status"
})(({ theme, $status }) => ({
    height: 20,
    fontSize: theme.typography.size.xs,
    borderColor: statusColor(theme, "invoice", $status),
    color: statusColor(theme, "invoice", $status)
}));

const ListState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary
}));

const BucketRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0)
}));

const BucketFigures = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm
}));

const ChoiceBlock = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2)
}));

const ChoiceTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    marginBottom: theme.spacing(0.5)
}));

const ChoiceOption = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing(1)
}));

const ChoiceHint = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
    marginLeft: theme.spacing(4)
}));

const formatHours = (value) => `${Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} h`;

const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatCount = (value, singular, plural) => `${Number(value || 0).toLocaleString("en-US")} ${Number(value) === 1 ? singular : plural}`;

const invoiceClientName = (invoice) => invoice?.client?.name || "Unknown client";

const invoiceClientId = (invoice) => {
    if (invoice?.client?._id) return String(invoice.client._id);
    return invoice?.client ? String(invoice.client) : null;
};

const monthLabelOfDay = (day) => formatMonthKey(`${day.slice(0, 4)}${day.slice(5, 7)}`);

const invoicePeriodLabel = (invoice) => {
    const start = formatDate(invoice?.billingPeriodStart);
    const end = formatDate(invoice?.billingPeriodEnd);
    if (!start && !end) return null;
    if (start && end) {
        if (start.slice(0, 7) === end.slice(0, 7)) return monthLabelOfDay(start);
        return `${formatDayShort(start)} to ${formatDayShort(end)}`;
    }
    return monthLabelOfDay(start || end);
};

const invoiceCoversMonth = (invoice, month) => {
    if (!month || !/^\d{4}-\d{2}$/.test(month)) return true;
    const start = formatDate(invoice?.billingPeriodStart);
    const end = formatDate(invoice?.billingPeriodEnd);
    if (!start && !end) return true;
    if (start && start.slice(0, 7) > month) return false;
    if (end && end < `${month}-01`) return false;
    return true;
};

const syncedGatewayLabel = (invoice) => {
    if (invoice?.hubspotId) return "HubSpot";
    if (invoice?.intuitId) return "Intuit";
    return null;
};

const byInvoiceDateDesc = (a, b) => {
    const left = a?.invoiceDate ? new Date(a.invoiceDate).getTime() : 0;
    const right = b?.invoiceDate ? new Date(b.invoiceDate).getTime() : 0;
    return right - left;
};

const isInMonth = (value, month) => {
    if (!month || !value) return false;
    return formatDate(value).slice(0, 7) === month;
};

const recordHours = (record) => {
    if (record?.hours != null) return Number(record.hours) || 0;
    return (Number(record?.time) || 0) / 60;
};

const buildBuckets = (data, month, selection) => {
    const timeByType = new Map();
    for (const record of data?.timeRecords || []) {
        if (!isInMonth(record?.date, month)) continue;
        const billingType = record?.resolvedBillingType || record?.billingType || "Unresolved";
        if (!timeByType.has(billingType)) timeByType.set(billingType, { key: billingType, kind: "time", label: billingType, ids: [], hours: 0 });
        const bucket = timeByType.get(billingType);
        bucket.ids.push(String(record._id));
        bucket.hours += recordHours(record);
    }

    const charges = { key: CHARGE_BUCKET, kind: "charges", label: "Charges", ids: [], amount: 0 };
    for (const charge of data?.charges || []) {
        if (!isInMonth(charge?.date, month)) continue;
        charges.ids.push(String(charge._id));
        charges.amount += Number(charge?.amount) || 0;
    }

    const buckets = [...timeByType.values()].sort((a, b) => b.hours - a.hours);
    if (charges.ids.length > 0) buckets.push(charges);

    const checked = {};
    for (const bucket of buckets) {
        checked[bucket.key] =
            bucket.kind === "charges"
                ? (selection?.chargeBillingTypes || []).length > 0
                : (selection?.timeBillingTypes || []).includes(bucket.key);
    }
    return { buckets, checked };
};

const MoveUnbilledToInvoiceDialog = ({ open, onClose, selection, month, monthLabel, onImported }) => {
    const dispatch = useDispatch();
    const requestSeq = useRef(0);
    const [step, setStep] = useState("pick");
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [invoices, setInvoices] = useState([]);
    const [total, setTotal] = useState(0);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const [resolving, setResolving] = useState(false);
    const [buckets, setBuckets] = useState([]);
    const [bucketChecked, setBucketChecked] = useState({});
    const [retainerMode, setRetainerMode] = useState("keep");
    const [rateMode, setRateMode] = useState("bill");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const clientName = selection?.clientName || "";
    const clientId = selection?.clientId || null;

    useEffect(() => {
        if (!open) return;
        requestSeq.current += 1;
        setStep("pick");
        setSearchInput(clientName);
        setSearch(clientName);
        setInvoices([]);
        setTotal(0);
        setSelectedInvoiceId(null);
        setBuckets([]);
        setBucketChecked({});
        setRetainerMode("keep");
        setRateMode("bill");
        setSubmitting(false);
        setResolving(false);
        setError(null);
    }, [open, clientName]);

    useEffect(() => {
        if (!open) return undefined;
        const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
        return () => clearTimeout(timer);
    }, [open, searchInput]);

    const loadInvoices = useCallback(() => {
        const seq = requestSeq.current + 1;
        requestSeq.current = seq;
        setLoadingInvoices(true);
        setSelectedInvoiceId(null);
        const opts = { search, sortBy: "invoiceDate", sortDir: "desc" };
        dispatch(
            getInvoices(
                0,
                INVOICE_PAGE_SIZE,
                "Draft",
                (draftResponse) => {
                    if (seq !== requestSeq.current) return;
                    if (!draftResponse) {
                        setLoadingInvoices(false);
                        setInvoices([]);
                        setTotal(0);
                        return;
                    }
                    dispatch(
                        getInvoices(
                            0,
                            INVOICE_PAGE_SIZE,
                            "Overdue",
                            (overdueResponse) => {
                                if (seq !== requestSeq.current) return;
                                setLoadingInvoices(false);
                                const drafts = draftResponse.invoices || [];
                                const overdue = overdueResponse?.invoices || [];
                                setInvoices([...drafts, ...overdue].sort(byInvoiceDateDesc));
                                setTotal((draftResponse.total || drafts.length) + (overdueResponse?.total || overdue.length));
                            },
                            opts
                        )
                    );
                },
                opts
            )
        );
    }, [dispatch, search]);

    useEffect(() => {
        if (!open) return;
        loadInvoices();
    }, [open, loadInvoices]);

    const selectedInvoice = useMemo(
        () => invoices.find((invoice) => String(invoice._id) === String(selectedInvoiceId)) || null,
        [invoices, selectedInvoiceId]
    );

    const rowBlockReason = useCallback(
        (invoice) => {
            const gateway = syncedGatewayLabel(invoice);
            if (gateway) return `Already sent to ${gateway}. Its total can no longer change, so nothing can be added to it.`;
            const rowClientId = invoiceClientId(invoice);
            if (clientId && rowClientId && rowClientId !== String(clientId)) {
                return `Belongs to ${invoiceClientName(invoice)}. The selected work belongs to ${clientName}, and one invoice carries one client's work.`;
            }
            return null;
        },
        [clientId, clientName]
    );

    const handleContinue = () => {
        if (!selectedInvoice || !month) return;
        setResolving(true);
        setError(null);
        dispatch(
            getInvoiceOrphans(
                selectedInvoice._id,
                (data) => {
                    setResolving(false);
                    if (!data) {
                        setError("The work that can go onto this invoice could not be read. Nothing has been changed — try again in a moment.");
                        return;
                    }
                    const resolved = buildBuckets(data, month, selection);
                    setBuckets(resolved.buckets);
                    setBucketChecked(resolved.checked);
                    setStep("confirm");
                },
                { includeAllUnbilled: true, month }
            )
        );
    };

    const chosen = useMemo(() => {
        const active = buckets.filter((bucket) => bucketChecked[bucket.key]);
        const timeBuckets = active.filter((bucket) => bucket.kind === "time");
        const chargeBuckets = active.filter((bucket) => bucket.kind === "charges");
        return {
            timeRecordIds: timeBuckets.flatMap((bucket) => bucket.ids),
            chargeIds: chargeBuckets.flatMap((bucket) => bucket.ids),
            hours: timeBuckets.reduce((sum, bucket) => sum + bucket.hours, 0),
            amount: chargeBuckets.reduce((sum, bucket) => sum + bucket.amount, 0),
            hasRetainer: timeBuckets.some((bucket) => bucket.key === "Retainer"),
            hasRated: timeBuckets.some((bucket) => !FLAT_BILLING_TYPES.has(bucket.key)),
            outsidePool: timeBuckets.filter((bucket) => !POOL_BILLING_TYPES.has(bucket.key)).map((bucket) => bucket.label)
        };
    }, [buckets, bucketChecked]);

    const chosenCount = chosen.timeRecordIds.length + chosen.chargeIds.length;
    const reportedCount = (selection?.reportedTimeCount || 0) + (selection?.reportedChargeCount || 0);
    const consumingRetainer = chosen.hasRetainer && retainerMode === "consume";

    const handleSubmit = () => {
        if (!selectedInvoice || chosenCount === 0) return;
        setSubmitting(true);
        setError(null);
        dispatch(
            addInvoiceOrphans(
                selectedInvoice._id,
                {
                    timeRecordIds: chosen.timeRecordIds,
                    chargeIds: chosen.chargeIds,
                    action: "add",
                    includeAllUnbilled: true,
                    keepBillingType: true,
                    zeroRate: chosen.hasRated && rateMode === "zero",
                    consumeRetainer: consumingRetainer
                },
                (response, failureMessage) => {
                    setSubmitting(false);
                    if (!response) {
                        setError(failureMessage || "The work could not be moved. Nothing was changed — the whole batch goes across or none of it does.");
                        return;
                    }
                    if (onImported) {
                        onImported({
                            invoiceNumber: selectedInvoice.invoiceNumber,
                            timeCount: chosen.timeRecordIds.length,
                            chargeCount: chosen.chargeIds.length,
                            hours: chosen.hours,
                            amount: chosen.amount,
                            retainerConsumption: response.retainerConsumption || null,
                            addedOutsidePeriod: response.addedOutsidePeriod || null
                        });
                    }
                }
            )
        );
    };

    const reportedLine = [
        selection?.reportedTimeCount > 0
            ? `${formatCount(selection.reportedTimeCount, "time record", "time records")} (${formatHours(selection.reportedHours)})`
            : null,
        selection?.reportedChargeCount > 0
            ? `${formatCount(selection.reportedChargeCount, "charge", "charges")} (${formatCurrency(selection.reportedAmount)})`
            : null
    ]
        .filter(Boolean)
        .join(" and ");

    const targetPeriod = selectedInvoice ? invoicePeriodLabel(selectedInvoice) : null;
    const differentPeriod = selectedInvoice ? !invoiceCoversMonth(selectedInvoice, month) : false;

    return (
        <Dialog open={!!open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
            <DialogTitle>{step === "pick" ? "Move unbilled work onto an invoice" : "Check this before it happens"}</DialogTitle>
            <DialogBody dividers>
                <Panel>
                    <PanelLabel variant="caption">Picked from {monthLabel}</PanelLabel>
                    <PanelValue variant="body1">{reportedLine || "Nothing selected"}</PanelValue>
                    <HelperLine variant="caption">
                        {clientName} &middot; {[...(selection?.timeBillingTypes || []), ...(selection?.chargeBillingTypes || [])].join(", ") || "no billing type"}
                    </HelperLine>
                </Panel>

                {step === "pick" ? (
                    <>
                        <HelperLine variant="body2">
                            Pick the invoice this work should be billed on. Only Draft and Overdue invoices can still take new lines — everything else has been
                            sent, paid or cancelled, and the system will refuse it.
                        </HelperLine>
                        <TextField
                            size="small"
                            fullWidth
                            label="Find an invoice"
                            placeholder="Invoice number or client name"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            helperText={
                                clientName && search === clientName
                                    ? `Prefilled with ${clientName}. Clear the box to search every open invoice.`
                                    : "Searches invoice numbers and client names."
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <InvoiceList>
                            {loadingInvoices ? (
                                <ListState>
                                    <CircularProgress size={22} />
                                    <Typography variant="body2">Looking for invoices that can still take new work.</Typography>
                                </ListState>
                            ) : invoices.length === 0 ? (
                                <ListState>
                                    <Typography variant="body2">
                                        No Draft or Overdue invoice matches that search. Generate an invoice for the period you want this work billed on, then
                                        come back.
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        If generating produces no invoice for this work, its billing terms are what need changing. Work only invoices at the
                                        first client, account or group above it that is a billing entity, and an internal client never invoices at all. Adjust
                                        the terms so the work has an invoice to belong to, then generate.
                                    </Typography>
                                </ListState>
                            ) : (
                                invoices.map((invoice) => {
                                    const blocked = rowBlockReason(invoice);
                                    const period = invoicePeriodLabel(invoice);
                                    return (
                                        <InvoiceRow
                                            key={invoice._id}
                                            $disabled={!!blocked}
                                            $selected={String(invoice._id) === String(selectedInvoiceId)}
                                            onClick={() => {
                                                if (blocked) return;
                                                setSelectedInvoiceId(invoice._id);
                                            }}
                                        >
                                            <Radio
                                                size="small"
                                                disabled={!!blocked}
                                                checked={String(invoice._id) === String(selectedInvoiceId)}
                                                onChange={() => setSelectedInvoiceId(invoice._id)}
                                                inputProps={{ "aria-label": `Choose invoice ${invoice.invoiceNumber}` }}
                                            />
                                            <InvoiceMain>
                                                <InvoiceHeadline>
                                                    <InvoiceNumber variant="body2">{invoice.invoiceNumber}</InvoiceNumber>
                                                    <StatusChip size="small" variant="outlined" label={invoice.status} $status={invoice.status} />
                                                </InvoiceHeadline>
                                                <InvoiceMeta variant="caption">
                                                    {invoiceClientName(invoice)}
                                                    {invoice.account?.name ? ` — ${invoice.account.name}` : ""}
                                                    {period ? ` — covers ${period}` : " — no billing period recorded"}
                                                </InvoiceMeta>
                                                {blocked && <InvoiceMeta variant="caption">{blocked}</InvoiceMeta>}
                                            </InvoiceMain>
                                            <InvoiceAmount variant="body2">{formatCurrency(invoice.amount)}</InvoiceAmount>
                                        </InvoiceRow>
                                    );
                                })
                            )}
                        </InvoiceList>
                        {!loadingInvoices && total > invoices.length && (
                            <HelperLine variant="caption">
                                Showing the {invoices.length} most recent of {total.toLocaleString("en-US")} open invoices. Search by invoice number to reach an
                                older one.
                            </HelperLine>
                        )}
                        {error && <Alert severity="error">{error}</Alert>}
                    </>
                ) : (
                    <>
                        <Panel>
                            <PanelLabel variant="caption">Goes onto</PanelLabel>
                            <PanelValue variant="body1">
                                {selectedInvoice?.invoiceNumber} — {invoiceClientName(selectedInvoice)}
                            </PanelValue>
                            <HelperLine variant="caption">
                                {selectedInvoice?.status} &middot; {targetPeriod ? `covers ${targetPeriod}` : "no billing period recorded"} &middot; currently{" "}
                                {formatCurrency(selectedInvoice?.amount)}
                            </HelperLine>
                        </Panel>

                        {buckets.length === 0 ? (
                            <Alert severity="warning">
                                None of the work picked in {monthLabel} can go onto {selectedInvoice?.invoiceNumber}. It either belongs to a different billing
                                entity than this invoice, or it has already been billed since the report was drawn up. Refresh the report and look again.
                            </Alert>
                        ) : (
                            <>
                                <Panel>
                                    <PanelLabel variant="caption">What moves</PanelLabel>
                                    {buckets.map((bucket) => (
                                        <BucketRow key={bucket.key}>
                                            <Checkbox
                                                size="small"
                                                checked={!!bucketChecked[bucket.key]}
                                                onChange={(event) => setBucketChecked((prev) => ({ ...prev, [bucket.key]: event.target.checked }))}
                                                inputProps={{ "aria-label": `Include ${bucket.label}` }}
                                            />
                                            <PanelValue variant="body2">{bucket.label}</PanelValue>
                                            <BucketFigures variant="caption">
                                                {formatCount(bucket.ids.length, "record", "records")} &middot;{" "}
                                                {bucket.kind === "charges" ? formatCurrency(bucket.amount) : formatHours(bucket.hours)}
                                            </BucketFigures>
                                        </BucketRow>
                                    ))}
                                    {reportedCount > 0 && chosenCount !== reportedCount && (
                                        <HelperLine variant="caption">
                                            The report counted {formatCount(reportedCount, "record", "records")} here.{" "}
                                            {formatCount(chosenCount, "record", "records")} of them can actually go onto this invoice — the rest belong to a
                                            different billing entity or have been billed since.
                                        </HelperLine>
                                    )}
                                </Panel>

                                {differentPeriod && (
                                    <Alert severity="info">
                                        This work is dated {monthLabel}, but {selectedInvoice?.invoiceNumber} covers {targetPeriod || "another period"}. The
                                        client will be billed for it on that invoice instead of a {monthLabel} one. That is the point — a {monthLabel} invoice
                                        that has already gone out can no longer be changed.
                                    </Alert>
                                )}

                                {chosen.hasRetainer && (
                                    <ChoiceBlock>
                                        <ChoiceTitle variant="body2">What should this do to the retainer?</ChoiceTitle>
                                        <HelperLine variant="caption">
                                            This decides whether the client is charged for the retainer hours being moved. Pick deliberately.
                                        </HelperLine>
                                        <RadioGroup value={retainerMode} onChange={(event) => setRetainerMode(event.target.value)}>
                                            <ChoiceOption>
                                                <FormControlLabel value="keep" control={<Radio />} label="Add it without touching the retainer" />
                                                <ChoiceHint variant="caption">
                                                    The work is listed on the invoice at $0 and the retainer&apos;s hours are left exactly as they are. Use this
                                                    when an earlier period already paid for the work and the line is only there so the client can see it.
                                                </ChoiceHint>
                                            </ChoiceOption>
                                            <ChoiceOption>
                                                <FormControlLabel value="consume" control={<Radio />} label="Add it and use up the retainer" />
                                                <ChoiceHint variant="caption">
                                                    The hours count against {targetPeriod || "this invoice"}&apos;s retainer and are priced exactly as a normal
                                                    billing run would price them. Whatever fits in the hours still left is included at no charge. Anything past
                                                    that is charged as overage if this retainer allows overage, and absorbed at no charge if it does not — so
                                                    this can add money to the invoice.
                                                </ChoiceHint>
                                            </ChoiceOption>
                                        </RadioGroup>
                                    </ChoiceBlock>
                                )}

                                {consumingRetainer && chosen.outsidePool.length > 0 && (
                                    <Alert severity="warning">
                                        A retainer pool can only absorb retainer and hourly work. {chosen.outsidePool.join(" and ")} is picked as well, and the
                                        move will be refused while it stays picked. Untick it above, or choose &ldquo;Add it without touching the
                                        retainer&rdquo;.
                                    </Alert>
                                )}

                                {chosen.hasRated && !consumingRetainer && (
                                    <ChoiceBlock>
                                        <ChoiceTitle variant="body2">How should the hourly work be priced?</ChoiceTitle>
                                        <RadioGroup value={rateMode} onChange={(event) => setRateMode(event.target.value)}>
                                            <ChoiceOption>
                                                <FormControlLabel value="bill" control={<Radio />} label="Bill it at the client's rate" />
                                                <ChoiceHint variant="caption">
                                                    Each record is priced from the client&apos;s rate for the skill on the work. Records with no rate stop the
                                                    whole move and are named back to you.
                                                </ChoiceHint>
                                            </ChoiceOption>
                                            <ChoiceOption>
                                                <FormControlLabel value="zero" control={<Radio />} label="Add it at $0" />
                                                <ChoiceHint variant="caption">
                                                    The hours still appear on the invoice, the money does not. Use this when the work has already been paid for.
                                                </ChoiceHint>
                                            </ChoiceOption>
                                        </RadioGroup>
                                    </ChoiceBlock>
                                )}

                                <HelperLine variant="body2">
                                    All {formatCount(chosenCount, "record", "records")} move together. If even one of them cannot be matched to this
                                    invoice&apos;s billing entity, nothing is added at all and the invoice is left exactly as it is.
                                </HelperLine>
                            </>
                        )}

                        {error && <Alert severity="error">{error}</Alert>}
                    </>
                )}
            </DialogBody>
            <DialogActions>
                {step === "pick" ? (
                    <>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            variant="contained"
                            disabled={!selectedInvoice || resolving}
                            onClick={handleContinue}
                            startIcon={resolving ? <CircularProgress size={16} /> : null}
                        >
                            {resolving ? "Checking…" : "Continue"}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={() => setStep("pick")} disabled={submitting}>
                            Back
                        </Button>
                        <Button onClick={onClose} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitting || chosenCount === 0}
                            startIcon={submitting ? <CircularProgress size={16} /> : null}
                        >
                            {submitting
                                ? "Moving…"
                                : consumingRetainer
                                  ? `Move ${chosenCount.toLocaleString("en-US")} and use the retainer`
                                  : `Move ${chosenCount.toLocaleString("en-US")} onto ${selectedInvoice?.invoiceNumber || "the invoice"}`}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default MoveUnbilledToInvoiceDialog;
